// FROM https://dev.to/n8tb1t/tracking-scroll-position-with-react-hooks-3bbj
import { useLayoutEffect, useRef } from 'react';

interface Position {
  x: number;
  y: number;
}

interface ScrollPosition {
  prevPos: Position;
  currPos: Position;
}

type ScrollEffect = (scrollPosition: ScrollPosition) => void;
type ElementSelector = { current?: HTMLElement };

const isBrowser = typeof window !== `undefined`;

function getScrollPosition({
  element,
  useWindow,
}: {
  element?: ElementSelector;
  useWindow: boolean;
}) {
  if (!isBrowser) return { x: 0, y: 0 };

  const target = element ? element.current : document.body;
  const position = target.getBoundingClientRect();

  return useWindow
    ? { x: window.scrollX, y: window.scrollY }
    : { x: position.left, y: position.top };
}

function useScrollPosition(
  effect: ScrollEffect,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  deps: any[],
  element?: ElementSelector,
  useWindow?: boolean,
  wait?: number
) {
  const position = useRef(getScrollPosition({ useWindow }));
  const throttleTimeoutRef = useRef(null);

  const callBack = () => {
    const currPos = getScrollPosition({ element, useWindow });
    effect({ prevPos: position.current, currPos });
    position.current = currPos;
    throttleTimeoutRef.current = null;
  };

  useLayoutEffect(() => {
    const handleScroll = () => {
      if (wait) {
        if (throttleTimeoutRef.current === null) {
          throttleTimeoutRef.current = setTimeout(callBack, wait);
        }
      } else {
        callBack();
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export default useScrollPosition;
